// js/sections/secondary-navigation.js

const observer = function observeElementAtTop(el, offset = "0px 0px 0px 0px", callback, threshold = 0) {
  let margin = offset.includes(" ") ? offset : `-${offset}px 0px 0px 0px`;
  const observer = new IntersectionObserver(
    ([entry]) => {
      callback(entry);
    },
    {
      root: null,
      rootMargin: margin,
      threshold: threshold,
    }
  );

  observer.observe(el);
  return observer;
};

function isView(element, callback, options = {}) {
  const { margin = "0px", amount = 0, root = null, once = false } = options;

  let hasTriggered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isInView = entry.intersectionRatio >= amount;

        if (once && hasTriggered && !isInView) {
          return;
        }

        if (isInView && once) {
          hasTriggered = true;
        }

        callback(isInView, entry);
      });
    },
    {
      root,
      rootMargin: margin,
      threshold: typeof amount === "number" ? [amount] : amount,
    }
  );

  observer.observe(element);

  // 返回清理函数，用于停止观察
  return () => observer.unobserve(element);
}

var SecondaryNavigation = class extends HTMLElement {
  constructor() {
    super();
    this.menuItems = [];
    this.currentIndex = 0;
    this._scrollHandler = null;
    this._headerContainer = null;
    this._sectionWrapper = null; // Add this line
    this._lastScrollY = 0;
    this._isHeaderVisible = true;
    this._product2508Observer = null;
  }

  connectedCallback() {
    this._sectionWrapper = this.closest(".section-secondary-navigation"); // Find the section wrapper
    this._init();
    this._setupScrollListener();
    this._setupEventListeners();
  }

  _init() {
    // 获取所有菜单项配置
    this.menuItems = Array.from(this.querySelectorAll(".secondary-nav-item")).map((item) => ({
      element: item,
      title: item.getAttribute("data-title") || item.textContent.trim(),
      targetId: item.getAttribute("data-target-id"),
      targetElement: null,
    }));

    // 查找对应的目标元素
    this.menuItems.forEach((item) => {
      if (item.targetId) {
        item.targetElement = document.getElementById(item.targetId);
      }
    });

    // 获取一级菜单容器
    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      this.dataset.headerHeight = this._headerContainer.getBoundingClientRect().height;
      this._setupProduct2508Observer();
    }

    // 设置初始状态
    this._updateActiveItem(0);
  }

  _setupScrollListener() {
    this._scrollHandler = this._throttle(() => {
      this._handleHeaderVisibility();
    }, 50);

    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    this._resizeHandler = this._throttle(() => {
      this._handleHeaderVisibilityResize();
    }, 50);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  _handleHeaderVisibilityResize() {
    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      // 更新 headerHeight
      this.dataset.headerHeight = this._headerContainer.getBoundingClientRect().height;

      // 重新设置 product2508 observer
      this._setupProduct2508Observer();

      // 更新 settoTop 位置，使用当前滚动方向
      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY > this._lastScrollY ? "down" : "up";
      this.settoTop(scrollDirection);
    }
  }

  _setupProduct2508Observer() {
    // 先清理现有的 observer
    if (this._product2508Observer) {
      this._product2508Observer.disconnect();
    }

    const product2508 = document.querySelector(".product-2508");
    if (product2508) {
      this._product2508Observer = observer(product2508, this.dataset.headerHeight, (entry) => {
        this.isIntersecting = entry.isIntersecting;
      });
    }
  }

  _setupEventListeners() {
    this.menuItems.forEach((item, index) => {
      item.element.addEventListener("click", (e) => {
        e.preventDefault();
        this._scrollToTarget(index);
      });

      // 只有当目标元素存在时才设置 inView 监听
      if (item.targetElement) {
        observer(
          item.targetElement,
          "-50% 0px 0px 0px",
          (isInView) => {
            if (isInView.isIntersecting) {
              this.currentIndex = index;
              this._updateActiveItem(index);
            }
          },
          0.5
        );
      }
    });
  }

  /**
   * 处理一级菜单的显示/隐藏
   */
  _handleHeaderVisibility() {
    const currentScrollY = window.scrollY;
    const scrollDirection = currentScrollY > this._lastScrollY ? "down" : "up";

    if (scrollDirection === "down" && !this.isIntersecting) {
      // 下滑隐藏
      this._hideHeader();
    } else if (scrollDirection === "up") {
      // 上滑显示
      this._showHeader();
    }
    this.settoTop(scrollDirection);
    this._lastScrollY = currentScrollY;
  }

  settoTop(scrollDirection) {
    if (!this._sectionWrapper) return;

    if (scrollDirection === "down") {
      this._sectionWrapper.style.top = "0px";
    } else {
      this._sectionWrapper.style.top = this.dataset.headerHeight + "px";
    }
  }
  _hideHeader() {
    if (this._headerContainer) {
      this._headerContainer.style.transform = "translateY(-100%)";
      this._headerContainer.style.transition = "transform 0.3s ease";
      this._isHeaderVisible = false;
    }
  }

  _showHeader() {
    if (this._headerContainer) {
      this._headerContainer.style.transform = "translateY(0)";
      this._headerContainer.style.transition = "transform 0.3s ease";
      this._isHeaderVisible = true;
    }
  }

  /**
   * 滚动到指定目标
   */
  _scrollToTarget(index) {
    const item = this.menuItems[index];
    if (!item || !item.targetElement) return;

    item.targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

    // 立即更新当前索引
    this.currentIndex = index;
    this._updateActiveItem(this.currentIndex);
  }

  /**
   * 更新激活状态
   */
  _updateActiveItem(index) {
    this.menuItems.forEach((item, i) => {
      item.element.classList.toggle("active", i === index);
      item.element.setAttribute("aria-current", String(i === index));
    });
  }

  /**
   * 节流函数
   */
  _throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
};

// 注册二级导航组件
if (!window.customElements.get("secondary-navigation")) {
  window.customElements.define("secondary-navigation", SecondaryNavigation);
}
